import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import messages from './messages';
import type { ActionLink, Props } from './types';

const ActionsCell = ({ id, actionLinks, onMenuClickHandleAnalytics }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { push: pushHistory } = useRouterActions();

	const [isDropdownMenuOpen, setIsDropdownMenuOpen] = useState(false);

	const handleDropdownMenuOpen = useCallback(() => {
		setIsDropdownMenuOpen(!isDropdownMenuOpen);

		if (onMenuClickHandleAnalytics != null) {
			const analyticsEvent = createAnalyticsEvent({
				actionSubject: 'dropdownMenu',
				action: 'clicked',
			});

			onMenuClickHandleAnalytics(analyticsEvent);
		}
	}, [createAnalyticsEvent, isDropdownMenuOpen, onMenuClickHandleAnalytics]);

	const handleDropdownMenuItemClick = useCallback(
		(link: ActionLink) => {
			const analyticsEvent = createAnalyticsEvent({
				actionSubject: 'dropdownItem',
				action: 'clicked',
			});

			if (link.onItemClickHandleAnalytics != null) {
				link.onItemClickHandleAnalytics(analyticsEvent);
			}
			if (ff('pce.helix.admin-pages.edit-workflow-page') && link.shouldPushHistory && link.href) {
				pushHistory(link.href);
			}
		},
		[createAnalyticsEvent, pushHistory],
	);

	return (
		<CellWrapper>
			<DropdownMenu
				key={id}
				isOpen={isDropdownMenuOpen}
				onOpenChange={handleDropdownMenuOpen}
				placement="bottom-end"
				trigger={({ triggerRef, ...props }) => (
					<Button
						{...props}
						iconBefore={<MoreIcon label={formatMessage(messages.actions)} />}
						ref={triggerRef}
						appearance="subtle"
					/>
				)}
			>
				<DropdownItemGroup>
					{actionLinks.map((link, index) => (
						<DropdownItem
							key={`action-link-${id}-${index}`}
							onClick={() => handleDropdownMenuItemClick(link)}
							href={
								ff('pce.helix.admin-pages.edit-workflow-page') && link.shouldPushHistory
									? undefined
									: link.href
							}
							UNSAFE_shouldDisableRouterLink
						>
							{link.displayMessage}
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</DropdownMenu>
		</CellWrapper>
	);
};

export default ActionsCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellWrapper = styled.div({
	display: 'flex',
	paddingTop: token('space.150', '12px'),
	boxSizing: 'border-box',
	height: token('space.600', '48px'),
});
